import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { useAPI } from 'contexts/APIProvider'
import React from 'react'
import { useParams } from 'react-router'
import { WorkReferenceForm } from 'types/interfaces'
import WorkReferenceFormError from 'errors/WorkReferenceFormError'
import update from 'immutability-helper'
import Header from 'components/Header'
import FormField from 'components/FormField'
import { Box, Button, Alert, MenuItem, Typography } from '@mui/material'
import ErrorList from 'components/ErrorList'
import { SkillAssessment } from 'types/types'
import NursePublicProfile from 'logic/NursePublicProfile'
import Loading from 'components/Loading'

const WorkReferenceRequest: React.FC = () => {
    const params = useParams()
    const { showAlert } = useSnackBarAlert()
    const { api } = useAPI()
    const [nurse, setNurse] = React.useState<NursePublicProfile>()
    const [loading, setLoading] = React.useState(false)
    const [form, setForm] = React.useState<WorkReferenceForm>({})
    const [error, setError] = React.useState<WorkReferenceFormError>()
    const [submitted, setSubmitted] = React.useState(false)

    const nurseId = React.useMemo(() => Number(params.nurseId), [params.nurseId])

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
        if (error) setError(update(error, { [name]: { $set: [] } }))
    }, [form, error])

    const fetchNurse = React.useCallback(async () => {
        try {
            const nurse = await api.getNursePublicProfile(nurseId)
            setNurse(nurse)
        } catch (error) {
            console.error('unhandled exception', error)
        }
    }, [api, nurseId])

    const submit = React.useCallback(async () => {
        try {
            setLoading(true)
            await api.createWorkReference(form)
            setSubmitted(true)
        } catch (error) {
            if (error instanceof WorkReferenceFormError) {
                setError(error)
            } else {
                console.error('unhandled exception', error)
            }
        } finally {
            setLoading(false)
        }
    }, [form, api, showAlert])

    const skillAccessmentOptions = React.useMemo(() => ([
        { label: 'Excellent', value: 'excellent' },
        { label: 'Good', value: 'good' },
        { label: 'Fair', value: 'fair' },
        { label: 'Poor', value: 'poor' },
    ]), [])

    const skillAccessmentMenuItems = React.useMemo(() => (
        skillAccessmentOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
                {option.label}
            </MenuItem>
        ))
    ), [skillAccessmentOptions])

    React.useEffect(() => {
        updateForm('nurse_id', nurseId)
    }, [nurseId])

    React.useEffect(() => {
        fetchNurse()
    }, [fetchNurse])

    if (!nurse) {
        return <Loading />
    }

    if (submitted) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Alert severity='success' variant='filled'>
                    Thank you for submitting your work reference for {nurse.full_name}
                </Alert>
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Header variant='h1' text={`Work Reference Request for ${nurse.full_name} (GDC Number: ${nurse.gdc_number})`}></Header>
            <FormField
                name='reference_name'
                label='What is your name?'
                type='text'
                onChange={(e) => updateForm('reference_name', e.target.value)}
                value={form.reference_name}
                errors={error?.reference_name}
            />
            <Header variant='h2' text='Skill Assessment' />
            <Typography variant='body1' color='text.secondary'>
                Please rate the dental hygienist's performance in the following areas
            </Typography>
            <FormField 
                select 
                value={form.patient_communication} 
                onChange={(e) => updateForm('patient_communication', e.target.value as SkillAssessment)}
                label='Patient Communication'
            >
                {skillAccessmentMenuItems}
            </FormField>
            <FormField
                select
                value={form.scaling_and_polishing}
                onChange={(e) => updateForm('scaling_and_polishing', e.target.value as SkillAssessment)}
                label='Scaling and Polishing'
            >
                {skillAccessmentMenuItems}
            </FormField>
            <FormField
                select
                value={form.infection_control_procedures}
                onChange={(e) => updateForm('infection_control_procedures', e.target.value as SkillAssessment)}
                label='Infection Control Procedures'
            >
                {skillAccessmentMenuItems}
            </FormField>
            <FormField
                select
                value={form.record_keeping_and_documentation}
                onChange={(e) => updateForm('record_keeping_and_documentation', e.target.value as SkillAssessment)} 
                label='Record Keeping and Documentation'
            >
                {skillAccessmentMenuItems}
            </FormField>
            <FormField
                select
                value={form.time_management}
                onChange={(e) => updateForm('time_management', e.target.value as SkillAssessment)}
                label='Time Management'
            >
                {skillAccessmentMenuItems}  
            </FormField>
            <FormField
                select
                value={form.teamwork_and_collaboration}
                onChange={(e) => updateForm('teamwork_and_collaboration', e.target.value as SkillAssessment)}
                label='Teamwork and Collaboration'
            >   
                {skillAccessmentMenuItems}
            </FormField>
            <FormField
                select
                value={form.professionalism}
                onChange={(e) => updateForm('professionalism', e.target.value as SkillAssessment)}  
                label='Professionalism'
            >
                {skillAccessmentMenuItems}
            </FormField>
            <Header variant='h2' text='Additional Comments' />
            <Typography variant='body1' color='text.secondary'>
                Any other feedback about the hygienist's clinical or non-clinical performance
            </Typography>
            <FormField
                name='comments'
                label='Comments'
                type='text'
                multiline
                rows={4}
                onChange={(e) => updateForm('comments', e.target.value)}
                value={form.comments}
                errors={error?.comments}
            />
            {error?.schema ? (
                <Alert severity='error' variant='filled'><ErrorList errors={error.schema} /></Alert>
            ) : null}
            <Button variant="contained" color="primary" onClick={submit} disabled={loading}>
                {loading ? 'Please Wait ...' : 'Submit'}
            </Button>
        </Box>
  )
}

export default WorkReferenceRequest